import { m } from '../../localization/out/messages.js';

const messages = m as unknown as Record<string, ((inputs?: object) => string) | undefined>;

/**
 * Resolves the paraglide `_field_*` key for a field path, mirroring the convention of
 * `translateFieldNameParaglide`. It is inlined here because importing `@adornis/field-management` pulls in the
 * deprecated client translator and its config requirements, which this module must stay free of to remain pure.
 */
const lookupFieldTranslation = (fieldPath: string | string[]) => {
  const key = `_field_${(Array.isArray(fieldPath) ? fieldPath : [fieldPath]).join('_')}`;
  return messages[key]?.() ?? key;
};

export interface TranslatedPathSegment {
  label: string;
  /** zero based position within an array field, i.e. the `0` in `kostenstelleProzentsaetze.0.prozentsatz` */
  index?: number;
}

const SEGMENT_SEPARATOR = ' › ';

const segmentCache = new Map<string, TranslatedPathSegment[]>();

const isNumericSegment = (segment: string) => /^\d+$/.test(segment);

/**
 * Turns a camelCase field name into something readable, used when no translation exists for a segment.
 * `kostenstelleProzentsaetze` becomes `Kostenstelle Prozentsaetze`, so the user never sees a raw translation key.
 */
const humanizeSegment = (segment: string) => {
  const spaced = segment
    .replace(/([a-zäöüß])([A-ZÄÖÜ])/g, '$1 $2')
    .replace(/([A-ZÄÖÜ]+)([A-ZÄÖÜ][a-zäöüß])/g, '$1 $2')
    .replace(/([a-zäöüß])(\d)/gi, '$1 $2')
    .trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const translateSegment = (segment: string) => {
  const translated = lookupFieldTranslation(segment);
  return translated.startsWith('_field_') ? humanizeSegment(segment) : translated;
};

/**
 * Translates a supplement change path into readable segments.
 *
 * Numeric path parts are not segments of their own, they become the index of the segment before them, so
 * `agEingruppierung1.agVertragsInfos.kostenstelleProzentsaetze.0.prozentsatz` reads as
 * `AG-Eingruppierung 1 › Vertragsinfos › Kostenstellen 1 › Prozentsatz`.
 *
 * This is purely for display, the stored path is never changed as it has to keep matching `writeToPath`.
 */
export const translateContractPathSegments = (path: string): TranslatedPathSegment[] => {
  const cached = segmentCache.get(path);
  if (cached) return cached;

  const rawSegments = path.split('.').filter(part => part.length > 0);
  const segments: TranslatedPathSegment[] = [];

  for (const rawSegment of rawSegments) {
    if (isNumericSegment(rawSegment)) {
      const previous = segments.at(-1);
      // a leading index has no field to attach to, so it becomes a segment of its own
      if (previous) previous.index = Number(rawSegment);
      else segments.push({ label: `#${Number(rawSegment) + 1}` });
      continue;
    }

    segments.push({ label: translateSegment(rawSegment) });
  }

  // without indices the fully joined key may exist as its own translation and is more precise than the parts
  if (!segments.some(segment => segment.index !== undefined) && rawSegments.length > 1) {
    const joined = lookupFieldTranslation(rawSegments);
    if (!joined.startsWith('_field_')) {
      const result = [{ label: joined }];
      segmentCache.set(path, result);
      return result;
    }
  }

  segmentCache.set(path, segments);
  return segments;
};

export const formatTranslatedPathSegment = (segment: TranslatedPathSegment) =>
  segment.index === undefined ? segment.label : `${segment.label} ${segment.index + 1}`;

/** Readable one line representation of a change path, i.e. for tooltips and plain text contexts. */
export const translateContractPath = (path: string, separator = SEGMENT_SEPARATOR) =>
  translateContractPathSegments(path).map(formatTranslatedPathSegment).join(separator);
